import RestApiImage from "assets/homepage/landing/buildingblocks/TriggerRestApi.svg";
import WebhookImage from "assets/homepage/landing/buildingblocks/TriggerWebhook.svg";
import Autoscaling from "assets/homepage/landing/buildingblocks/Autoscaling.png";
import OutputImage from "assets/homepage/landing/buildingblocks/DataOutputs.svg";

import { IBuildingBlock } from "./BuildingBlock";

const QueueDepthCode = `
from beam import QueueDepthAutoscaler

# Scale out when queue size > 30 tasks
autoscaling_config = QueueDepthAutoscaler(
    tasks_per_container=30,
    max_containers=300,
)`;

const WorkersCode = `
from beam import endpoint

@endpoint(workers=5, on_start=load_models)
def inference():
    llm = vllm.LLM(model="openai/gpt-oss-20b")
    return {"prediction": llm.generate("The future of AI is")[0].text}
`;

const RestApiExampleCode = `
from beam import Sandbox

sandbox = Sandbox(cpu=1).create()

sandbox.create_image_from_filesystem()
sandbox.snapshot_memory()
sandbox.terminate()
`;

const SandboxExampleCode = `
from beam import Image, Sandbox

sb = Sandbox(image=Image()).create()

sb.process.run_code("print('hello from the sandbox!')").result

sb.terminate()
`;

const SandboxSnapshotCode = `
from beam import Image, Sandbox

sb = Sandbox().create()
image_id = sb.create_image_from_filesystem()
sb.terminate()

sb = Sandbox(image=Image.from_id(image_id)).create()
`;

const SandboxBootCode = `
from beam import Volume, Sandbox

volume = Volume(name="documents", mount_path="/workspace/documents")
sb = Sandbox(keep_warm_seconds=3600, volumes=[volume], gpu="H100").create()

# Terminate sandbox after 5 minutes
sb.update_ttl(300)
`;

const WebhookExampleCode = `
from beam import task_queue

@task_queue(gpu="A10G", callback_url="ngrok.io")
def transcribe():
    model = whisper.load_model("small")
    model.transcribe("./meeting-notes")`;

const RunExampleCode = `
from beam import Image, Sandbox

sb = Sandbox(image=Image().from_registry("node:20")).create()
url = sb.expose_port(3000)

sb.process.exec("sh", "-c", "npx http-server -p 3000 -c-1")
`;

const PodExampleCode = `
from beam import Sandbox, Image, PythonVersion

sandbox = Sandbox(image=Image(python_version=PythonVersion.Python311))
sb = sandbox.create()

sb.fs.upload_file("my_script.py", "/workspace/my_script.py")`;

const BuildingBlocks: IBuildingBlock[] = [
  {
    type: "Agentic Environments",
    title: "Secure Runtime for Agents",
    items: [
      {
        name: "Coding Environments for AI Agents",
        description: `Provide your users with a secure environment to run and test agent-generated code. Built for AI agents, code interpreters, or any application that needs to execute untrusted code at scale.`,
        illustration: {
          layout: "1",
          imageSrc: WebhookImage,
          code: SandboxExampleCode,
        },
      },
      {
        name: "Stateful, Persistent Runtimes",
        description:
          "Sandboxes are stateful. You can connect to a running process, attach persistent storage volumes, and snapshot the file system to create reusable templates.",
        illustration: {
          layout: "1",
          imageSrc: WebhookImage,
          code: SandboxSnapshotCode,
        },
      },
      {
        name: "Ultrafast Boot Times",
        description:
          "Boot sandboxes in under a second. Build and launch 10 GiB container images in under three seconds. You've never iterated on a stack this fast.",
        illustration: {
          layout: "1",
          imageSrc: WebhookImage,
          code: SandboxBootCode,
        },
      },
    ],
  },
  {
    type: "Powerful Primitives",
    title: "Dynamic Container Environments",
    items: [
      {
        name: "Pause and Resume Sessions",
        description: `Capture the complete state of any sandbox - including filesystem, memory, and running processes - as an immutable snapshot.`,
        illustration: {
          layout: "2",
          code: RestApiExampleCode,
          imageSrc: RestApiImage,
        },
      },
      {
        name: "Bring your own Dockerfile",
        description: `Customize your sandbox environment with your own Dockerfile and any dependencies you need. You have full control over the environment.`,
        illustration: {
          layout: "1",
          code: RunExampleCode,
          imageSrc: OutputImage,
        },
      },
      {
        name: "Run File System Operations",
        description:
          "Upload local files, download files from the sandbox, list directories, and manage files with full metadata access.",
        illustration: {
          layout: "1",
          code: PodExampleCode,
          imageSrc: WebhookImage,
        },
      },
    ],
  },
  {
    type: "Autoscaling",
    title: "Elastic GPU Scaling",
    items: [
      {
        name: "Scale to zero when idle, burst to thousands in seconds",
        description: `Scale up and down with traffic. Pay only for actual compute time down to the CPU cycle.`,
        illustration: {
          layout: "1",
          imageSrc: Autoscaling,
          code: QueueDepthCode,
        },
      },
      {
        name: "Sub-second cold starts",
        description: `We provide distributed storage layer, memory snapshotting, and GPU checkpoint restore, resulting in the fastest container boot times of any provider.`,
        illustration: {
          layout: "1",
          imageSrc: Autoscaling,
          code: QueueDepthCode,
        },
      },
      {
        name: "Run workloads across clouds",
        description:
          "Unlike other providers, Beam runs on bare-metal servers around the world. You can scale without the constraints of a traditional cloud provider.",
        illustration: {
          layout: "1",
          imageSrc: Autoscaling,
          code: WorkersCode,
        },
      },
    ],
  },
];

export default BuildingBlocks;
